import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, TrendingUp, TrendingDown,
  Calendar, Bitcoin, Bell, BellOff, ChevronDown, ChevronUp, Clock, Settings
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ShareCardButton } from "../components/ShareCard";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Every Day" },
  { value: "weekly", label: "Every Week" },
  { value: "biweekly", label: "Every 2 Weeks" },
  { value: "monthly", label: "Every Month" },
];

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function JourneyTracker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState({ total_usd_invested: 0, total_btc_accumulated: 0, purchase_count: 0, stack_streak: 0, total_stack_days: 0 });
  const [btcPrice, setBtcPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ date: new Date().toISOString().split("T")[0], amount_usd: "", note: "" });
  const [saving, setSaving] = useState(false);

  // DCA Reminder state
  const [showDCA, setShowDCA] = useState(false);
  const [dcaReminder, setDcaReminder] = useState(null);
  const [dcaForm, setDcaForm] = useState({
    frequency: "weekly",
    amount_usd: 50,
    notify_method: "in_app",
    day_of_week: 0,
    day_of_month: 1,
    enabled: true
  });
  const [dcaSaving, setDcaSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchData = async () => {
    try {
      const [purchaseRes, priceRes] = await Promise.all([
        api.get("/journey/purchases"),
        api.get("/widgets/bitcoin-price")
      ]);
      setPurchases(purchaseRes.data.purchases || []);
      setStats({
        total_usd_invested: purchaseRes.data.total_usd_invested,
        total_btc_accumulated: purchaseRes.data.total_btc_accumulated,
        purchase_count: purchaseRes.data.purchase_count,
        stack_streak: purchaseRes.data.stack_streak || 0,
        total_stack_days: purchaseRes.data.total_stack_days || 0
      });
      setBtcPrice(priceRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchDCA = async () => {
    try {
      const [reminderRes, notifRes] = await Promise.all([
        api.get("/dca/reminder"),
        api.get("/dca/notifications")
      ]);
      if (reminderRes.data.reminder) {
        setDcaReminder(reminderRes.data.reminder);
        setDcaForm({
          frequency: reminderRes.data.reminder.frequency || "weekly",
          amount_usd: reminderRes.data.reminder.amount_usd || 50,
          notify_method: reminderRes.data.reminder.notify_method || "in_app",
          day_of_week: reminderRes.data.reminder.day_of_week ?? 0,
          day_of_month: reminderRes.data.reminder.day_of_month ?? 1,
          enabled: reminderRes.data.reminder.enabled ?? true
        });
      }
      setNotifications((notifRes.data.notifications || []).filter(n => !n.dismissed));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchData(); fetchDCA(); }, []);

  const currentValue = btcPrice ? stats.total_btc_accumulated * btcPrice.price : 0;
  const totalPnl = currentValue - stats.total_usd_invested;
  const pnlPercent = stats.total_usd_invested > 0 ? ((totalPnl / stats.total_usd_invested) * 100).toFixed(1) : 0;
  const avgCost = stats.purchase_count > 0 ? (stats.total_usd_invested / stats.total_btc_accumulated).toFixed(0) : 0;

  const addPurchase = async () => {
    if (!formData.amount_usd || parseFloat(formData.amount_usd) <= 0) {
      toast.error("Enter a valid USD amount");
      return;
    }
    if (!btcPrice) {
      toast.error("BTC price unavailable");
      return;
    }
    setSaving(true);
    try {
      const amountUsd = parseFloat(formData.amount_usd);
      const amountBtc = amountUsd / btcPrice.price;
      const res = await api.post("/journey/purchases", {
        date: formData.date,
        amount_usd: amountUsd,
        amount_btc: parseFloat(amountBtc.toFixed(8)),
        price_at_purchase: btcPrice.price,
        note: formData.note
      });
      toast.success("Purchase logged!");
      if (res.data.new_badges?.length > 0) {
        const badgeNames = { stack_streak_7: "7-Day Stacker", stack_streak_30: "30-Day Stacker", stack_streak_90: "90-Day Diamond Hands", stack_streak_365: "Full Year Stacker" };
        res.data.new_badges.forEach(b => toast.success(`Stack Streak Badge: ${badgeNames[b] || b}!`));
      }
      if (res.data.stack_streak > 0) {
        toast(`${res.data.stack_streak}-day stack streak!`, { icon: "⚡" });
      }
      setFormData({ date: new Date().toISOString().split("T")[0], amount_usd: "", note: "" });
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to save purchase");
    }
    setSaving(false);
  };

  const deletePurchase = async (id) => {
    try {
      await api.delete(`/journey/purchases/${id}`);
      toast.success("Purchase removed");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const saveDCAReminder = async () => {
    if (dcaForm.amount_usd <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setDcaSaving(true);
    try {
      await api.post("/dca/reminder", dcaForm);
      toast.success("DCA reminder saved!");
      fetchDCA();
      setShowDCA(false);
    } catch (err) {
      toast.error("Failed to save reminder");
    }
    setDcaSaving(false);
  };

  const deleteDCAReminder = async () => {
    try {
      await api.delete("/dca/reminder");
      setDcaReminder(null);
      setDcaForm({ frequency: "weekly", amount_usd: 50, notify_method: "in_app", day_of_week: 0, day_of_month: 1, enabled: true });
      toast.success("DCA reminder deleted");
      fetchDCA();
    } catch {
      toast.error("Failed to delete reminder");
    }
  };

  const dismissNotification = async (id) => {
    try {
      await api.post(`/dca/notifications/${id}/dismiss`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="journey-tracker-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="journey-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">JOURNEY <span className="text-[#F7931A]">TRACKER</span></h1>
          <div className="flex items-center gap-2">
            <ShareCardButton
              type="journey_stats"
              data={{
                user_name: user?.first_name || "",
                total_btc: stats.total_btc_accumulated.toFixed(8),
                purchase_count: stats.purchase_count,
                pnl_percent: pnlPercent,
                avg_cost: Number(avgCost).toLocaleString()
              }}
              shareText={`My Bitcoin Journey on Bitcoin Brain: ${stats.total_btc_accumulated.toFixed(8)} BTC stacked across ${stats.purchase_count} purchases. ${totalPnl >= 0 ? "+" : ""}${pnlPercent}% P&L`}
              shareUrl="https://bitcoinbrain.space/journey"
            />
            <Button onClick={() => setShowForm(!showForm)} size="sm" className="btn-primary" data-testid="add-purchase-btn">
              <Plus size={16} className="mr-1" /> Log
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* DCA Notifications Banner */}
        <AnimatePresence>
          {notifications.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              {notifications.slice(0, 2).map(n => (
                <div key={n.id} className="bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl p-4 mb-2 flex items-center gap-3" data-testid="dca-notification">
                  <Bell className="text-[#F7931A] flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{n.message}</p>
                    {n.btc_price > 0 && (
                      <p className="text-zinc-500 text-xs mt-0.5">
                        BTC: ${n.btc_price?.toLocaleString()} | ~{n.sats_equivalent?.toLocaleString()} sats
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="btn-primary text-xs h-7" onClick={() => { setShowForm(true); setFormData(prev => ({ ...prev, amount_usd: String(n.amount_usd) })); dismissNotification(n.id); }} data-testid="dca-log-now-btn">
                      Log Now
                    </Button>
                    <button onClick={() => dismissNotification(n.id)} className="text-zinc-600 hover:text-zinc-400">
                      <ChevronUp size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Portfolio Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="card-dark p-4">
            <p className="text-zinc-500 text-xs mb-1">Total Invested</p>
            <p className="text-white font-mono font-bold text-lg" data-testid="total-invested">${stats.total_usd_invested.toLocaleString()}</p>
          </div>
          <div className="card-dark p-4">
            <p className="text-zinc-500 text-xs mb-1">BTC Accumulated</p>
            <p className="text-[#F7931A] font-mono font-bold text-lg" data-testid="total-btc">{stats.total_btc_accumulated.toFixed(8)}</p>
          </div>
          <div className="card-dark p-4">
            <p className="text-zinc-500 text-xs mb-1">Current Value</p>
            <p className="text-white font-mono font-bold text-lg">${currentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
          </div>
          <div className="card-dark p-4">
            <p className="text-zinc-500 text-xs mb-1">P&L</p>
            <div className="flex items-center gap-1">
              {totalPnl >= 0 ? <TrendingUp size={16} className="text-green-400" /> : <TrendingDown size={16} className="text-red-400" />}
              <p className={`font-mono font-bold text-lg ${totalPnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                {totalPnl >= 0 ? "+" : ""}{pnlPercent}%
              </p>
            </div>
          </div>
        </div>

        {/* DCA Stats */}
        {stats.purchase_count > 0 && (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-xs">Average Cost Basis</p>
              <p className="text-white font-mono font-bold">${Number(avgCost).toLocaleString()} / BTC</p>
            </div>
            <div className="text-right">
              <p className="text-zinc-500 text-xs">Purchases</p>
              <p className="text-[#F7931A] font-mono font-bold">{stats.purchase_count}</p>
            </div>
            {btcPrice && (
              <div className="text-right">
                <p className="text-zinc-500 text-xs">BTC Price Now</p>
                <p className="text-white font-mono font-bold">${btcPrice.price?.toLocaleString()}</p>
              </div>
            )}
          </div>
        )}

        {/* Stack Streak */}
        {stats.purchase_count > 0 && (
          <div className="bg-gradient-to-r from-[#F7931A]/10 to-transparent border border-[#F7931A]/20 rounded-xl p-4" data-testid="stack-streak-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                  <span className="text-xl">{stats.stack_streak >= 30 ? "🔥" : stats.stack_streak >= 7 ? "⚡" : stats.stack_streak > 0 ? "📈" : "💔"}</span>
                </div>
                <div>
                  <p className="text-white font-bold text-lg font-mono" data-testid="stack-streak-count">
                    {stats.stack_streak > 0 ? `${stats.stack_streak}-day streak` : "Streak broken"}
                  </p>
                  <p className="text-zinc-500 text-xs">{stats.total_stack_days} total stacking days</p>
                </div>
              </div>
              <div className="text-right">
                {stats.stack_streak > 0 ? (
                  <>
                    <p className="text-zinc-500 text-xs mb-1">Next badge</p>
                    <p className="text-[#F7931A] text-sm font-medium">
                      {stats.stack_streak < 7 ? `${7 - stats.stack_streak} days to 7-Day Stacker` :
                       stats.stack_streak < 30 ? `${30 - stats.stack_streak} days to 30-Day Stacker` :
                       stats.stack_streak < 90 ? `${90 - stats.stack_streak} days to Diamond Hands` :
                       stats.stack_streak < 365 ? `${365 - stats.stack_streak} days to Full Year Stacker` :
                       "All streak badges earned!"}
                    </p>
                  </>
                ) : (
                  <Button
                    onClick={async () => {
                      try {
                        const res = await api.post("/xp-store/use-streak-saver");
                        toast.success(res.data.message);
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to save streak");
                      }
                    }}
                    className="bg-[#F7931A] text-black text-xs px-3 py-1.5 h-auto font-bold"
                    data-testid="streak-saver-btn"
                  >
                    Save Streak (500 BTC XP)
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DCA Reminder Section */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden" data-testid="dca-reminder-section">
          <button
            onClick={() => setShowDCA(!showDCA)}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            data-testid="dca-toggle-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                <Bell className="text-[#F7931A]" size={18} />
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-sm">DCA Reminders</p>
                <p className="text-zinc-500 text-xs">
                  {dcaReminder
                    ? `${dcaReminder.enabled ? "Active" : "Paused"} — $${dcaReminder.amount_usd} ${dcaReminder.frequency}`
                    : "Set up automatic buy reminders"}
                </p>
              </div>
            </div>
            {showDCA ? <ChevronUp className="text-zinc-500" size={18} /> : <ChevronDown className="text-zinc-500" size={18} />}
          </button>

          <AnimatePresence>
            {showDCA && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-4">
                  {/* Frequency */}
                  <div>
                    <label className="text-zinc-500 text-xs block mb-2">Frequency</label>
                    <div className="grid grid-cols-4 gap-2">
                      {FREQUENCY_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setDcaForm(prev => ({ ...prev, frequency: opt.value }))}
                          className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                            dcaForm.frequency === opt.value
                              ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                              : "bg-[#161616] border-white/10 text-zinc-400 hover:border-white/20"
                          }`}
                          data-testid={`dca-freq-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Day selector for weekly/biweekly */}
                  {(dcaForm.frequency === "weekly" || dcaForm.frequency === "biweekly") && (
                    <div>
                      <label className="text-zinc-500 text-xs block mb-2">Day of Week</label>
                      <div className="flex gap-1.5">
                        {DAY_LABELS.map((d, i) => (
                          <button
                            key={i}
                            onClick={() => setDcaForm(prev => ({ ...prev, day_of_week: i }))}
                            className={`flex-1 p-2 rounded-lg text-xs font-medium border transition-all ${
                              dcaForm.day_of_week === i
                                ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                                : "bg-[#161616] border-white/10 text-zinc-500 hover:border-white/20"
                            }`}
                            data-testid={`dca-day-${i}`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Day of month for monthly */}
                  {dcaForm.frequency === "monthly" && (
                    <div>
                      <label className="text-zinc-500 text-xs block mb-2">Day of Month</label>
                      <input
                        type="number"
                        min="1"
                        max="28"
                        value={dcaForm.day_of_month}
                        onChange={e => setDcaForm(prev => ({ ...prev, day_of_month: Math.min(28, Math.max(1, parseInt(e.target.value) || 1)) }))}
                        className="w-24 bg-[#161616] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                        data-testid="dca-day-of-month"
                      />
                    </div>
                  )}

                  {/* Amount */}
                  <div>
                    <label className="text-zinc-500 text-xs block mb-2">Amount (USD)</label>
                    <div className="flex gap-2">
                      {[10, 25, 50, 100].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setDcaForm(prev => ({ ...prev, amount_usd: amt }))}
                          className={`flex-1 p-2 rounded-lg text-xs font-mono font-medium border transition-all ${
                            dcaForm.amount_usd === amt
                              ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                              : "bg-[#161616] border-white/10 text-zinc-400 hover:border-white/20"
                          }`}
                          data-testid={`dca-amount-${amt}`}
                        >
                          ${amt}
                        </button>
                      ))}
                      <input
                        type="number"
                        value={dcaForm.amount_usd}
                        onChange={e => setDcaForm(prev => ({ ...prev, amount_usd: parseFloat(e.target.value) || 0 }))}
                        className="w-24 bg-[#161616] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-[#F7931A]/50 focus:outline-none"
                        placeholder="Custom"
                        data-testid="dca-custom-amount"
                      />
                    </div>
                  </div>

                  {/* Notification method */}
                  <div>
                    <label className="text-zinc-500 text-xs block mb-2">Notification</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDcaForm(prev => ({ ...prev, notify_method: "in_app" }))}
                        className={`flex-1 p-2 rounded-lg text-xs font-medium border transition-all flex items-center justify-center gap-1.5 ${
                          dcaForm.notify_method === "in_app"
                            ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                            : "bg-[#161616] border-white/10 text-zinc-400"
                        }`}
                        data-testid="dca-notify-app"
                      >
                        <Bell size={14} /> In-App
                      </button>
                      <button
                        onClick={() => setDcaForm(prev => ({ ...prev, notify_method: "email" }))}
                        className={`flex-1 p-2 rounded-lg text-xs font-medium border transition-all flex items-center justify-center gap-1.5 ${
                          dcaForm.notify_method === "email"
                            ? "bg-[#F7931A]/20 border-[#F7931A]/50 text-[#F7931A]"
                            : "bg-[#161616] border-white/10 text-zinc-400"
                        }`}
                        data-testid="dca-notify-email"
                      >
                        <Clock size={14} /> Email
                      </button>
                    </div>
                  </div>

                  {/* Preview */}
                  {dcaForm.amount_usd > 0 && btcPrice && (
                    <div className="bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-lg p-3">
                      <p className="text-[#F7931A] text-sm font-mono">
                        ${dcaForm.amount_usd} {dcaForm.frequency} = ~{((dcaForm.amount_usd / btcPrice.price) * 100_000_000).toFixed(0)} sats at current price
                      </p>
                      <p className="text-zinc-600 text-xs mt-1">
                        {dcaForm.frequency === "weekly" ? `~$${(dcaForm.amount_usd * 52).toLocaleString()}/year` :
                         dcaForm.frequency === "biweekly" ? `~$${(dcaForm.amount_usd * 26).toLocaleString()}/year` :
                         dcaForm.frequency === "monthly" ? `~$${(dcaForm.amount_usd * 12).toLocaleString()}/year` :
                         `~$${(dcaForm.amount_usd * 365).toLocaleString()}/year`}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button onClick={saveDCAReminder} disabled={dcaSaving} className="btn-primary flex-1" data-testid="save-dca-btn">
                      {dcaSaving ? "Saving..." : dcaReminder ? "Update Reminder" : "Set Reminder"}
                    </Button>
                    {dcaReminder && (
                      <Button onClick={deleteDCAReminder} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-400/10" data-testid="delete-dca-btn">
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Add Purchase Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="bg-[#0A0A0A] border border-[#F7931A]/30 rounded-xl p-5 space-y-4 overflow-hidden">
              <h3 className="font-heading text-lg font-bold text-white">Log Bitcoin Purchase</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Date</label>
                  <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                    className="w-full bg-[#161616] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                    data-testid="purchase-date-input" />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Amount (USD)</label>
                  <input type="number" step="0.01" value={formData.amount_usd} onChange={e => setFormData({ ...formData, amount_usd: e.target.value })}
                    placeholder="50.00"
                    className="w-full bg-[#161616] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                    data-testid="purchase-amount-input" />
                </div>
              </div>
              {formData.amount_usd && btcPrice && (
                <div className="bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-lg p-3">
                  <p className="text-[#F7931A] text-sm font-mono">
                    = {(parseFloat(formData.amount_usd) / btcPrice.price).toFixed(8)} BTC at ${btcPrice.price?.toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Note (optional)</label>
                <input type="text" value={formData.note} onChange={e => setFormData({ ...formData, note: e.target.value })}
                  placeholder="DCA, dip buy, gift..."
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
                  data-testid="purchase-note-input" />
              </div>
              <div className="flex gap-3">
                <Button onClick={addPurchase} disabled={saving} className="btn-primary flex-1" data-testid="save-purchase-btn">
                  {saving ? "Saving..." : "Save Purchase"}
                </Button>
                <Button onClick={() => setShowForm(false)} variant="ghost" className="text-zinc-400">Cancel</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Purchase History */}
        <div>
          <h3 className="font-heading text-lg font-bold text-white mb-3">Purchase History</h3>
          {purchases.length === 0 ? (
            <div className="card-dark p-8 text-center">
              <Bitcoin className="text-zinc-700 mx-auto mb-3" size={40} />
              <p className="text-zinc-500 text-sm">No purchases logged yet.</p>
              <p className="text-zinc-600 text-xs mt-1">Start tracking your Bitcoin journey!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {purchases.map((p, i) => {
                const currentVal = btcPrice ? p.amount_btc * btcPrice.price : 0;
                const pnl = currentVal - p.amount_usd;
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                    className="card-dark p-4 flex items-center gap-4" data-testid={`purchase-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="text-[#F7931A]" size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">${p.amount_usd.toLocaleString()}</p>
                        <span className="text-zinc-600">&rarr;</span>
                        <p className="text-[#F7931A] font-mono text-sm">{p.amount_btc.toFixed(8)} BTC</p>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-zinc-600 text-xs">{p.date}</p>
                        <span className="text-zinc-700">&middot;</span>
                        <p className="text-zinc-600 text-xs">${p.price_at_purchase?.toLocaleString()}/BTC</p>
                        {p.note && <><span className="text-zinc-700">&middot;</span><p className="text-zinc-500 text-xs truncate">{p.note}</p></>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-mono text-sm ${pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {pnl >= 0 ? "+" : ""}{((pnl / p.amount_usd) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <button onClick={() => deletePurchase(p.id)} className="text-zinc-700 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`delete-purchase-${i}`}>
                      <Trash2 size={16} />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
